import { useQuery } from '@tanstack/react-query';

import PortainerError from '@/portainer/error';
import { useCurrentUser } from '@/react/hooks/useUser';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { isAxiosError } from '@/portainer/services/axios/utils/isAxiosError';
import { UserId } from '@/portainer/users/types';

import { AccessToken } from '../types';

import { queryKeys } from './query-keys';
import { buildUrl } from './build-url';

const DEFAULT_RETRY_COUNT = 3;
const UNRETRYABLE_STATUSES = [403, 404];

class AccessTokensUnavailableError extends PortainerError {}

export function useAccessTokens(
  userId?: UserId,
  { enabled }: { enabled?: boolean } = {}
) {
  const { user } = useCurrentUser();
  const id = userId ?? user.Id;

  return useQuery({
    queryKey: queryKeys.base(id),
    queryFn: ({ signal }) => getAccessTokens(id, { signal }),
    enabled,
    retry: (failureCount, error) =>
      !(error instanceof AccessTokensUnavailableError) &&
      failureCount < DEFAULT_RETRY_COUNT,
  });
}

async function getAccessTokens(
  userId: number,
  options: { signal?: AbortSignal } = {}
) {
  try {
    const { data } = await axios.get<Array<AccessToken>>(buildUrl(userId), {
      signal: options.signal,
    });
    return data;
  } catch (e) {
    const err = parseAxiosError(e, 'Unable to get access tokens');
    if (
      isAxiosError(e) &&
      e.response &&
      UNRETRYABLE_STATUSES.includes(e.response.status)
    ) {
      throw new AccessTokensUnavailableError(err.message, err.err);
    }

    throw err;
  }
}
